import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import { Card, Button } from '../components';
import { BelKnop } from '../components/BelKnop';
import {
  MessageCircle,
  Send,
  Search,
  User,
  Clock,
  Building2,
  Users,
  GraduationCap,
  Shield
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { chatAPI, Conversation, Message } from '../services/chatService';
import { useSocket } from '../hooks/useSocket';
import { formatDistanceToNow } from 'date-fns';
import { nl } from 'date-fns/locale';

type ChatTab = 'alle' | 'belagom' | 'opdrachtgever' | 'professionals' | 'opleiders';

const BerichtenPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const { user } = useAuthStore();
  useCockpitTitle(t('berichten.title'), t('berichten.subtitle'));
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [selectedConversation, setSelectedConversation] = useState<string | null>(
    searchParams.get('conversation') || null
  );
  const [activeTab, setActiveTab] = useState<ChatTab>(
    (searchParams.get('tab') as ChatTab) || 'alle'
  );
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const TAB_CONFIG: { id: ChatTab; label: string; icon: React.ComponentType<{ className?: string }>; filter: (c: Conversation) => boolean }[] = [
    { id: 'alle', label: t('berichten.tabs.all'), icon: MessageCircle, filter: () => true },
    { id: 'belagom', label: t('berichten.tabs.belagom'), icon: Shield, filter: (c) => c.type === 'admin_broadcast' || c.type === 'professional_belagom' || c.type === 'opleider_belagom' },
    { id: 'opdrachtgever', label: t('berichten.tabs.employer'), icon: Building2, filter: (c) => c.type === 'professional_employer' },
    { id: 'professionals', label: t('berichten.tabs.professionals'), icon: Users, filter: (c) => c.type === 'professional_peer' || c.type === 'professional_group' },
    { id: 'opleiders', label: t('berichten.tabs.trainers'), icon: GraduationCap, filter: (c) => c.type === 'opleider_professional' || c.type === 'opleider_employer' || c.type === 'opleider_groep' },
  ];

  const isProfessional = user?.role === 'professional';

  // Sync state from URL (one-way: URL → state only, dep on string not object)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const conversationId = params.get('conversation');
    if (conversationId !== selectedConversation) {
      setSelectedConversation(conversationId);
    }
    const tabParam = (params.get('tab') as ChatTab) || 'alle';
    if (tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleTabChange = (tab: ChatTab) => {
    setActiveTab(tab);
    setSelectedConversation(null);
    setSearchTerm('');
    const newParams: Record<string, string> = {};
    if (tab !== 'alle') newParams.tab = tab;
    setSearchParams(newParams, { replace: true });
  };

  const handleSelectConversation = (id: string) => {
    setSelectedConversation(id);
    const newParams: Record<string, string> = {};
    if (activeTab !== 'alle') newParams.tab = activeTab;
    newParams.conversation = id;
    setSearchParams(newParams, { replace: true });
  };

  // Fetch conversations
  const { data: conversations, isLoading: conversationsLoading } = useQuery({
    queryKey: ['conversations'],
    queryFn: async () => {
      return await chatAPI.getConversations();
    },
    enabled: !!user,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Fetch messages for selected conversation
  const { data: messages, isLoading: messagesLoading } = useQuery({
    queryKey: ['messages', selectedConversation],
    queryFn: async () => {
      if (!selectedConversation) return [];
      return await chatAPI.getMessages(selectedConversation);
    },
    enabled: !!selectedConversation,
  });

  // Real-time message handler
  const handleNewMessage = (message: Message) => {
    if (String(message.conversation_id) === String(selectedConversation)) {
      // Add message to current conversation
      queryClient.setQueryData(['messages', selectedConversation], (old: Message[] = []) => {
        // Check if message already exists
        if (old.some(m => m.id === message.id)) {
          return old;
        }
        return [...old, message];
      });

      // Scroll to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }

    // Invalidate conversations to update unread count and last message
    queryClient.invalidateQueries({ queryKey: ['conversations'] });
  };

  // Typing indicator handler
  const handleUserTyping = (userId: string, isTyping: boolean) => {
    // Could show typing indicator in UI
    console.log('User typing:', userId, isTyping);
  };

  // Message read handler
  const handleMessageRead = (userId: string, messageId: string) => {
    // Could update read status in UI
    console.log('Message read:', userId, messageId);
  };

  // Notification handler
  const handleNotification = (notification: any) => {
    if (notification.type === 'new_message' && notification.conversation_id !== selectedConversation) {
      toast.success(t('berichten.newMessageReceived'));
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    }
  };

  // Socket hook for real-time updates
  const { sendMessage: sendMessageViaSocket, startTyping, stopTyping, markAsRead, typingUsers, isConnected: socketConnected } = useSocket({
    conversationId: selectedConversation || undefined,
    onNewMessage: handleNewMessage,
    onUserTyping: handleUserTyping,
    onMessageRead: handleMessageRead,
    onNotification: handleNotification
  });

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messages && messages.length > 0) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [messages]);

  // Mark messages as read when conversation is selected
  useEffect(() => {
    if (selectedConversation && messages && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage && lastMessage.sender_id !== user?.id) {
        markAsRead(selectedConversation, lastMessage.id);
      }
    }
  }, [selectedConversation, messages, user?.id, markAsRead]);

  // Handle typing indicator
  const handleInputChange = (value: string) => {
    setNewMessage(value);

    if (selectedConversation) {
      if (value.trim().length > 0) {
        startTyping(selectedConversation);

        // Clear existing timeout
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }

        // Stop typing after 3 seconds of no input
        typingTimeoutRef.current = setTimeout(() => {
          stopTyping(selectedConversation);
        }, 3000);
      } else {
        stopTyping(selectedConversation);
      }
    }
  };

  const sendMessageMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!selectedConversation) throw new Error('No conversation selected');

      // IMPORTANT: Do NOT send via both REST and Socket.IO (causes duplicate DB messages).
      // Prefer Socket.IO for real-time delivery; fallback to REST if socket isn't connected.
      if (socketConnected) {
        sendMessageViaSocket(selectedConversation, content);
        return null as any;
      }

      return await chatAPI.sendMessage(selectedConversation, content);
    },
    onSuccess: () => {
      setNewMessage('');
      stopTyping(selectedConversation || '');
      queryClient.invalidateQueries({ queryKey: ['messages', selectedConversation] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (error: any) => {
      console.error('Error sending message:', error);
      toast.error(error?.response?.data?.message || t('berichten.sendError'));
    }
  });

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    sendMessageMutation.mutate(newMessage.trim());
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const activeTabFilter = TAB_CONFIG.find(t => t.id === activeTab)?.filter ?? (() => true);

  const filteredConversations = conversations?.filter(conversation => {
    if (!activeTabFilter(conversation)) return false;
    if (!searchTerm) return true;
    return conversation.participant
      ? (
          conversation.participant.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          conversation.participant.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          conversation.onderwerp?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          conversation.last_message?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : conversation.onderwerp?.toLowerCase().includes(searchTerm.toLowerCase());
  }) || [];

  // Unread count per tab
  const tabUnread = (tab: ChatTab) => {
    const filter = TAB_CONFIG.find(t => t.id === tab)?.filter ?? (() => true);
    return conversations?.filter(filter).reduce((sum, c) => sum + (c.unread_count || 0), 0) || 0;
  };

  const selectedConvData = conversations?.find(c => c.id === selectedConversation);
  const otherParticipant = selectedConvData?.participant;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Chat category tabs - only for professionals */}
      {isProfessional && (
        <div className="berichten-page-tabs mb-6 border-b border-gray-200">
          <nav className="berichten-tab-nav -mb-px flex space-x-1 overflow-x-auto">
            {TAB_CONFIG.map((tab) => {
              const Icon = tab.icon;
              const unread = tab.id === 'alle' ? 0 : tabUnread(tab.id);
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`group inline-flex items-center gap-2 px-4 py-3 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? 'border-[#0B1F3A] text-[#0B1F3A]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span>{tab.label}</span>
                  {unread > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                      {unread > 99 ? '99+' : unread}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 h-auto lg:h-[calc(100vh-200px)]">
        {/* Conversations List */}
        <div className="lg:col-span-1">
          <Card className="h-full flex flex-col">
            {/* Search */}
            <div className="p-4 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder={t('berichten.searchPlaceholder')}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Conversations */}
            <div className="flex-1 overflow-y-auto">
              {conversationsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="text-center py-12">
                  <MessageCircle className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">{t('berichten.noConversations')}</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {t('berichten.noConversationsHint')}
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredConversations.map((conversation) => (
                    <div
                      key={conversation.id}
                      onClick={() => handleSelectConversation(conversation.id)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedConversation === conversation.id ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        {conversation.participant?.profile_photo ? (
                          <img
                            src={conversation.participant.profile_photo}
                            alt={`${conversation.participant.first_name} ${conversation.participant.last_name}`}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="w-5 h-5 text-gray-500" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {conversation.participant
                                ? `${conversation.participant.first_name} ${conversation.participant.last_name}`
                                : conversation.onderwerp}
                            </p>
                            {conversation.unread_count && conversation.unread_count > 0 && (
                              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full">
                                {conversation.unread_count}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 truncate mt-1">
                            {conversation.last_message || t('berichten.noMessages')}
                          </p>
                          {conversation.last_message_at && (
                            <div className="flex items-center mt-1 text-xs text-gray-400">
                              <Clock className="w-3 h-3 mr-1" />
                              {formatDistanceToNow(new Date(conversation.last_message_at), {
                                addSuffix: true,
                                locale: nl
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Messages Area */}
        <div className="lg:col-span-2">
          <Card className="h-full flex flex-col">
            {selectedConversation ? (
              <>
                {/* Messages Header */}
                <div className="p-4 border-b">
                  <div className="flex items-center space-x-3">
                    {otherParticipant?.profile_photo ? (
                      <img
                        src={otherParticipant.profile_photo}
                        alt={`${otherParticipant.first_name} ${otherParticipant.last_name}`}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="w-5 h-5 text-gray-500" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {otherParticipant
                          ? `${otherParticipant.first_name} ${otherParticipant.last_name}`
                          : selectedConvData?.onderwerp}
                      </h3>
                      {typingUsers.length > 0 && (
                        <p className="text-sm text-gray-500 italic">{t('berichten.typing')}</p>
                      )}
                    </div>
                    <BelKnop conversationId={selectedConversation} />
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messagesLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : messages?.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageCircle className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">{t('berichten.noMessagesYet')}</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {t('berichten.sendFirstMessage')}
                      </p>
                    </div>
                  ) : (
                    <>
                      {messages?.map((message) => {
                        const isOwnMessage = message.sender_id === user?.id;
                        return (
                          <div
                            key={message.id}
                            className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                              isOwnMessage
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-200 text-gray-900'
                            }`}>
                              {!isOwnMessage && message.first_name && (
                                <p className="text-xs font-medium mb-1 opacity-75">
                                  {message.first_name} {message.last_name}
                                </p>
                              )}
                              <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                              <p className={`text-xs mt-1 ${
                                isOwnMessage ? 'text-blue-100' : 'text-gray-500'
                              }`}>
                                {formatDistanceToNow(new Date(message.created_at), {
                                  addSuffix: true,
                                  locale: nl
                                })}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Message Input */}
                <div className="p-4 border-t">
                  <div className="flex space-x-4">
                    <textarea
                      rows={1}
                      placeholder={t('berichten.messagePlaceholder')}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      value={newMessage}
                      onChange={(e) => handleInputChange(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={sendMessageMutation.isPending}
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim() || sendMessageMutation.isPending}
                    >
                      {sendMessageMutation.isPending ? (
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <MessageCircle className="mx-auto h-16 w-16 text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">{t('berichten.selectConversation')}</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    {t('berichten.selectConversationHint')}
                  </p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BerichtenPage;
